import { useCallback, useEffect, useMemo, useState } from "react";

import { isEqual, omit } from "lodash";
import { StringParam, useQueryParam } from "use-query-params";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useLogger } from "@components/v2/analytics";
import {
  type AmplitudeEventContainer,
  type AmplitudeNativeEventType,
} from "@north-beam/nb-client-side";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import { isDefaultPageConfiguration, type PageConfiguration } from "../context";
import { type TopLevelBreakdown } from "../types";

import { isDefaultChartState, useChartState } from "./use-chart-state";
import { usePageConfiguration } from "./use-page-configuration";
import {
  useSavedViewsQueries,
  type SavedView,
} from "./use-saved-views-queries";
import { isDefaultTableState, useTableState } from "./use-table-state";

function findSavedView(
  savedViews: SavedView[],
  id: string | number | undefined | null,
): SavedView | undefined {
  return savedViews.find((savedView) => {
    if (id === "default") {
      return true;
    }
    return savedView.id === Number(id);
  });
}

/* Each client has different breakdown choices so this is for a template to
   select all choices for a breakdown */
const getTemplateBreakdownsForSavedView = ({
  pageConfiguration,
  breakdownConfigs,
}: {
  pageConfiguration: PageConfiguration;
  breakdownConfigs: CategoricalBreakdownConfig[];
}) => {
  const { breakdowns } = pageConfiguration;
  if (!breakdowns.length) {
    return breakdowns;
  }

  return breakdowns.map((breakdown: TopLevelBreakdown) => {
    const associatedConfig = breakdownConfigs.find(
      (config) => config.key === breakdown.key,
    );

    const shouldUseConfigChoices =
      associatedConfig && breakdown.values.length === 0;

    return shouldUseConfigChoices
      ? {
          key: breakdown.key,
          values: associatedConfig.choices.map(({ value }) => value),
        }
      : breakdown;
  });
};

export const useSavedViews = () => {
  const logger = useLogger({});
  const { chartState, setChartState } = useChartState();
  const { pageConfiguration, setPageConfiguration } = usePageConfiguration();
  const { tableState, setTableState } = useTableState();
  const { value: breakdownConfigs, loading: isBreakdownLoading } =
    useSalesBreakdownConfigs();

  const [selectedSavedViewId, setSelectedSavedViewId] = useQueryParam(
    "savedViewId",
    StringParam,
    { updateType: "pushIn" },
  );

  const {
    savedViews,
    updateSavedViewOrdering,
    updateSavedView,
    createSavedView: _createSavedView,
    createHiddenSavedViewTemplate,
    deleteSavedView,
  } = useSavedViewsQueries();

  const [selectedSavedView, setSelectedSavedView] = useState<SavedView | null>(
    null,
  );

  const selectSavedView = useCallback(
    // If the context is not provided, the event will not be logged.
    (
      maybeSavedView: SavedView | null | undefined,
      context: {
        eventType: AmplitudeNativeEventType;
        container: AmplitudeEventContainer;
      } | null,
    ) => {
      const savedView =
        findSavedView(savedViews, maybeSavedView?.id) || maybeSavedView;

      if (savedView) {
        setSelectedSavedViewId(String(savedView.id), "pushIn");
        setChartState(savedView.config.chartState, "pushIn");
        setPageConfiguration(savedView.config.pageConfiguration);
        setTableState(
          {
            ...savedView.config.tableState,
            columnConfigs: savedView.config.tableState.columnConfigs.map((i) =>
              omit(i, "name"),
            ),
          },
          "pushIn",
        );
        setSelectedSavedView(savedView);
        if (context) {
          logger.event.selectSalesSavedView(context.eventType, {
            container: context.container,
            eventDetails: {
              savedView: {
                id: savedView.id,
                name: savedView.name,
                isTemplate: savedView.is_template,
              },
            },
          });
        }
      }
    },
    [
      logger.event,
      savedViews,
      setSelectedSavedViewId,
      setChartState,
      setPageConfiguration,
      setTableState,
      setSelectedSavedView,
    ],
  );

  const createSavedView = useCallback(
    async (
      name: SavedView["name"],
      config: SavedView["config"],
      context: {
        eventType: AmplitudeNativeEventType;
        container: AmplitudeEventContainer;
      } | null,
    ) => {
      // TODO: Fix the typings on the mutation here at the API - this is really bad.
      const result: SavedView = await _createSavedView(name, config);
      if (context) {
        logger.event.createSalesSavedView(context.eventType, {
          container: context.container,
          eventDetails: {
            savedView: {
              id: result.id,
              name: result.name,
            },
          },
        });
      }
      return result;
    },
    [logger.event, _createSavedView],
  );

  // Selects first saved view if none is selected
  useEffect(() => {
    if (!selectedSavedViewId && savedViews.length > 0) {
      selectSavedView(savedViews[0], null);
    }
  }, [savedViews, selectedSavedViewId, selectSavedView]);

  useEffect(() => {
    const savedView = findSavedView(savedViews, selectedSavedViewId);
    setSelectedSavedView(savedView || null);
  }, [savedViews, selectedSavedViewId, setSelectedSavedView]);

  // Selects saved view when saved views are loaded
  useEffect(() => {
    if (!selectedSavedView && selectedSavedViewId && !isBreakdownLoading) {
      const savedView = findSavedView(savedViews, selectedSavedViewId);

      if (savedView) {
        const {
          config: { chartState, pageConfiguration, tableState: _tableState },
          is_default,
        } = savedView;

        setPageConfiguration((prevPageConfiguration) => {
          const pageConfigurationCopy = { ...pageConfiguration };
          if (is_default && pageConfigurationCopy.breakdowns.length) {
            const templateBreakdowns = getTemplateBreakdownsForSavedView({
              pageConfiguration: pageConfigurationCopy,
              breakdownConfigs,
            });
            pageConfigurationCopy.breakdowns = templateBreakdowns;
          }

          if (
            isEqual(prevPageConfiguration, pageConfigurationCopy) ||
            isDefaultPageConfiguration(prevPageConfiguration)
          ) {
            return pageConfigurationCopy;
          }
          return prevPageConfiguration;
        }, "pushIn");

        setChartState((prevChartState) => {
          if (
            isEqual(prevChartState, chartState) ||
            isDefaultChartState(prevChartState)
          ) {
            return chartState;
          }
          return prevChartState;
        }, "pushIn");

        const tableState = {
          ..._tableState,
          columnConfigs: _tableState.columnConfigs.map((i) => omit(i, "name")),
        };

        setTableState((prevTableState) => {
          if (
            isEqual(prevTableState, tableState) ||
            isDefaultTableState(prevTableState)
          ) {
            return tableState;
          }
          return prevTableState;
        }, "pushIn");

        setSelectedSavedView((prevSavedView) => {
          if (prevSavedView?.id === savedView.id) {
            return prevSavedView;
          }
          return savedView;
        });
      }
    }
  }, [
    isBreakdownLoading,
    breakdownConfigs,
    savedViews,
    selectedSavedView,
    selectedSavedViewId,
    setChartState,
    setPageConfiguration,
    setTableState,
    setSelectedSavedView,
  ]);

  const isStateSaved = useMemo(() => {
    if (!selectedSavedView) {
      return false;
    }

    const savedPageConfigs = selectedSavedView.config.pageConfiguration;
    const isPageConfigEqual = isEqual(
      {
        ...pageConfiguration,
        breakdowns: pageConfiguration.breakdowns.map((i) => i.key),
      },
      {
        ...savedPageConfigs,
        breakdowns: savedPageConfigs.breakdowns.map((i) => i.key),
      },
    );

    const isChartStateEqual = isEqual(
      chartState.chartConfigs,
      selectedSavedView.config.chartState.chartConfigs,
    );

    const isTableStateEqual = isEqual(tableState, {
      ...selectedSavedView.config.tableState,
      columnConfigs: selectedSavedView.config.tableState.columnConfigs.map(
        (i) => omit(i, "name"),
      ),
    });

    return isPageConfigEqual && isChartStateEqual && isTableStateEqual;
  }, [selectedSavedView, pageConfiguration, chartState, tableState]);

  const saveNewSavedView = async (
    name: string,
    context: {
      eventType: AmplitudeNativeEventType;
      container: AmplitudeEventContainer;
    } | null,
  ) => {
    const savedViewConfig = {
      pageConfiguration,
      chartState,
      tableState,
    };
    const newSavedView = await createSavedView(name, savedViewConfig, context);
    // Do not log the event if we are automatically selecting the saved view after it was created.
    selectSavedView(newSavedView, null);
    return newSavedView;
  };

  const updateSavedViewName = async (id: number, name: string) =>
    updateSavedView({ id, name });
  const updateSavedViewConfig = async () => {
    if (selectedSavedViewId) {
      const savedViewConfig = {
        pageConfiguration,
        chartState,
        tableState,
      };
      await updateSavedView({
        id: Number(selectedSavedViewId),
        config: savedViewConfig,
      });
    }
  };

  return {
    savedViews,
    isStateSaved,
    selectedSavedView,
    updateSavedViewName,
    updateSavedViewConfig,
    updateSavedViewOrdering,
    createSavedView: saveNewSavedView,
    createHiddenSavedViewTemplate,
    selectSavedView,
    deleteSavedView,
  };
};
